<div
  class="drawer-overlay"
  *ngIf="visible"
  [class.drawer-overlay--nested-open]="nestedManagementDrawerOpen"
  (click)="onOverlayClick()"
></div>

<div class="drawer" [class.drawer--open]="visible">
  <ng-container *ngIf="user">
    <div class="drawer__topbar">
      <button class="drawer__close" (click)="close()">
        <i class="pi pi-times"></i>
      </button>
    </div>

    <div class="drawer__header">
      <bo-users-management-user-avatar
        [avatar]="user.avatar"
        [firstName]="user.firstName ?? ''"
        [lastName]="user.lastName ?? ''"
        size="xlarge"
        styleClass="user-avatar--drawer"
      />

      <div class="drawer__header-info" [class.name-is-long]="isNameWrapped">
        <div class="drawer__header-name">
          <h2 #nameRef>
            <ng-container *ngIf="user.hasProfile !== false; else emailHeader">
              {{ user.firstName }} {{ user.lastName }}
            </ng-container>
            <ng-template #emailHeader>{{ user.email }}</ng-template>
          </h2>
        </div>
        <span class="drawer__created" *ngIf="formatCreatedOn(user.createdAt) as createdOn">
          Created on {{ createdOn }}
        </span>
        <span class="drawer__no-profile" *ngIf="user.hasProfile === false">
          No profile yet — add first and last name to create one.
        </span>
      </div>

      <div class="drawer__header-status">
        <span class="status-badge" [ngClass]="getStatusBadgeClass(user.status)">
          {{ user.status }}
        </span>
        <span class="drawer__last-login" *ngIf="formatLastLoginOn(user.lastLoginAt) as lastLogin">
          {{ 'userDetail.lastLoginOn' | translate }} <strong>{{ lastLogin }}</strong>
        </span>
      </div>
    </div>

    <div class="drawer__user-type">
      <span class="drawer__user-type-label">
        {{ 'userDetail.userType' | translate }} <strong>{{ user.userType?.label ?? 'User' }}</strong>
      </span>
      <img
        *ngIf="user.userType as type"
        [src]="getUserTypeIconPath(type.key)"
        [alt]="type.label"
        class="drawer__user-type-icon"
      />
    </div>

    <div class="detail-toast" *ngIf="toastVisible" [ngClass]="'toast--' + toastType">
      <div class="toast__icon">
        <i
          [class]="
            toastType === 'success'
              ? 'pi pi-check'
              : toastType === 'error'
              ? 'pi pi-trash'
              : 'pi pi-exclamation-triangle'
          "
        >
        </i>
      </div>
      <div class="toast__message">{{ toastMessage }}</div>
      <button class="toast__close" (click)="hideToast()"><i class="pi pi-times"></i></button>
    </div>
    <div class="drawer__body">
      <section class="section">
        <div class="section__title-row">
          <span class="section__title">{{ 'userDetail.personalInfo' | translate }}</span>
          <button class="btn-edit" *ngIf="!isEditing" (click)="onEdit()">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            {{ 'userDetail.edit' | translate }}
          </button>
          <button class="btn-discard" *ngIf="isEditing" (click)="onDiscard()"><i class="pi pi-undo"></i> {{ 'userDetail.undo' | translate }}</button>
        </div>
        <div class="info-grid" *ngIf="!isEditing">
          <div class="info-field">
            <label>{{ 'userDetail.firstName' | translate }}</label>
            <span>{{ user.firstName || '—' }}</span>
          </div>
          <div class="info-field">
            <label>{{ 'userDetail.lastName' | translate }}</label>
            <span>{{ user.lastName || '—' }}</span>
          </div>
          <div class="info-field">
            <label>{{ 'userDetail.phone' | translate }}</label>
            <span>{{ user.phone }}</span>
          </div>
          <div class="info-field">
            <label>{{ 'userDetail.email' | translate }}</label>
            <span>{{ user.email }}</span>
          </div>
        </div>
        <div class="info-grid info-grid--edit" *ngIf="isEditing">
          <div class="info-field">
            <label>{{ 'userDetail.firstName' | translate }}</label>
            <input
              pInputText
              [(ngModel)]="editForm.firstName"
              (blur)="onFieldBlur('firstName')"
              [class.ng-invalid]="!!getFieldError('firstName') || hasIncompleteProfileNameError"
              [class.ng-dirty]="touchedFields.has('firstName')"
              (input)="onFieldInput('firstName')"
              [placeholder]="user.hasProfile === false ? 'Optional — fill both names to create profile' : ''"
            />
            <small class="field-error" *ngIf="getFieldError('firstName')">
              {{ getFieldError('firstName') }}
            </small>
          </div>
          <div class="info-field">
            <label>{{ 'userDetail.lastName' | translate }}</label>
            <input
              pInputText
              [(ngModel)]="editForm.lastName"
              (blur)="onFieldBlur('lastName')"
              [class.ng-invalid]="!!getFieldError('lastName') || hasIncompleteProfileNameError"
              [class.ng-dirty]="touchedFields.has('lastName')"
              (input)="onFieldInput('lastName')"
              [placeholder]="user.hasProfile === false ? 'Optional — fill both names to create profile' : ''"
            />
            <small class="field-error" *ngIf="getFieldError('lastName')">
              {{ getFieldError('lastName') }}
            </small>
            <small class="field-error" *ngIf="hasIncompleteProfileNameError">
              {{ 'userDetail.nameNeeded' | translate }}
            </small>
          </div>
          <div class="info-field info-field--full">
            <label>{{ 'userDetail.phone' | translate }}</label>
            <input
              pInputText
              [(ngModel)]="editForm.phone"
              (blur)="onFieldBlur('phone')"
              [class.ng-invalid]="!!getFieldError('phone')"
              [class.ng-dirty]="touchedFields.has('phone')"
              (input)="onFieldInput('phone')"
              [placeholder]="'userDetail.enterPhone' | translate"
            />
            <small class="field-error" *ngIf="getFieldError('phone')">
              {{ getFieldError('phone') }}
            </small>
          </div>
          <div class="info-field info-field--full">
            <label>{{ 'userDetail.email' | translate }}</label>
            <input
              pInputText
              [(ngModel)]="editForm.email"
              (blur)="onFieldBlur('email')"
              [class.ng-invalid]="!!getFieldError('email')"
              [class.ng-dirty]="touchedFields.has('email')"
              (input)="onFieldInput('email')"
              placeholder="example@email.com"
            />
            <small class="field-error" *ngIf="getFieldError('email')">
              {{ getFieldError('email') }}
            </small>
          </div>
        </div>
      </section>
      <section class="section">
        <div class="section__title-row">
          <span class="section__title">{{ 'userDetail.sportsLevel' | translate }}</span>
          <button class="btn-edit" *ngIf="!isEditingSports" (click)="startEditSports()">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            {{ 'userDetail.edit' | translate }}
          </button>
          <button class="btn-discard" *ngIf="isEditingSports" (click)="discardSports()">
            <i class="pi pi-undo"></i> {{ 'userDetail.undo' | translate }}
          </button>
        </div>

        <div class="sport-list">
          <div class="sport-card" *ngFor="let sport of user.sports; let i = index">
            <div class="sport-card__header">
              <div class="sport-card__title">{{ sport.name }}</div>
              <div class="sport-card__date">{{ sport.joinedAt }}</div>
            </div>

            <div class="sport-card__body">
              <div *ngIf="!isEditingSports" class="sport-card__level">{{ sport.level }} / 10</div>
              <div *ngIf="isEditingSports" class="sport-card__edit">
                <p-dropdown [options]="levelOptions" [(ngModel)]="sport.level"></p-dropdown>
              </div>
            </div>
          </div>

          <div class="sport-empty" *ngIf="!user.sports || user.sports.length === 0">{{ 'userDetail.noSports' | translate }}</div>
        </div>
      </section>
      <div
        class="drawer__locked-sections"
        [class.drawer__locked-sections--disabled]="anyEditing"
        [attr.inert]="anyEditing ? '' : null"
        [attr.aria-hidden]="anyEditing ? true : null"
      >
      <section class="section">
        <div class="section__title-row">
          <span class="section__title">{{ 'userDetail.position' | translate }}</span>
        </div>
        <div class="map-container">
          <iframe
            *ngIf="mapUrl"
            [src]="mapUrl"
            width="100%"
            height="200"
            style="border: 0; border-radius: 8px"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          >
          </iframe>
          <div class="map-placeholder" *ngIf="!mapUrl">
            <i class="pi pi-map-marker"></i>
            <span>{{ 'userDetail.noLocation' | translate }}</span>
          </div>
        </div>
      </section>
      <section class="section">
        <div class="section__title-row">
          <span class="section__title">{{ 'userDetail.pushNotification' | translate }}</span>
        </div>
        <div class="notification-item">
          <div class="notification-item__icon">
            <i class="pi pi-bell"></i>
          </div>

          <div class="notification-item__content">
            <span class="notification-item__title">{{ 'userDetail.pushNotification' | translate }}</span>
            <span class="notification-item__desc">{{ 'userDetail.willReceivePush' | translate }}</span>
          </div>

          <span
            class="notif-badge"
            [ngClass]="user.notification ? 'notif-badge--on' : 'notif-badge--off'"
          >
            {{ user.notification ? 'On' : 'Off' }}
          </span>
        </div>
      </section>
      <section class="section">
        <div class="section__title-row">
          <span class="section__title">{{ 'userDetail.activities' | translate }}</span>
        </div>
        <div class="tab-bar">
          <button
            class="tab-btn"
            [class.tab-btn--active]="activeActivityTab === 0"
            (click)="onActivityTabChange(0)"
          >
            Incoming ({{ incomingCount }})
          </button>
          <button
            class="tab-btn"
            [class.tab-btn--active]="activeActivityTab === 1"
            (click)="onActivityTabChange(1)"
          >
            Completed ({{ completedCount }})
          </button>
          <button
            class="tab-btn"
            [class.tab-btn--active]="activeActivityTab === 2"
            (click)="onActivityTabChange(2)"
          >
            Cancelled ({{ cancelledCount }})
          </button>
        </div>
        <div class="activity-list">
          <div
            class="activity-item"
            *ngFor="let act of visibleActivities"
            (click)="openActivityDetail(act)"
            style="cursor: pointer"
          >
            <div class="activity-item__img" [class.activity-item__img--sport]="isSportIconUrl(act.image)">
              <div *ngIf="isSportIconUrl(act.image); else activityPhoto" class="sport-icon-thumb-wrap">
                <span
                  class="sport-icon-thumb"
                  [style.mask-image]="'url(' + act.image + ')'"
                  [style.-webkit-mask-image]="'url(' + act.image + ')'"
                ></span>
              </div>
              <ng-template #activityPhoto>
                <img [src]="act.image" alt="" (error)="onActivityImageError($event, act)" />
              </ng-template>
            </div>

            <div class="activity-item__content">
              <div class="activity-item__top">
                <span class="activity-item__date-time"><i class="pi pi-calendar-plus"></i> {{ act.schedule }}</span>
                <span class="role-badge" *ngIf="act.role" [ngClass]="getRoleBadgeClass(act.role)">{{ act.role }}</span>
              </div>

              <div class="activity-item__title">{{ act.title }}</div>

              <div class="activity-item__meta">
                <div class="activity-item__meta-left">
                  <div class="meta-row">
                    <i class="pi pi-map-marker"></i>
                    <span>{{ act.location || act.venue || 'Unknown location' }}</span>
                  </div>
                  <div class="meta-inline">
                    <div class="meta-row">
                      <span
                        class="sport-icon"
                        [style.mask-image]="'url(' + getSportIconPath(act.sportKey) + ')'"
                        [style.-webkit-mask-image]="'url(' + getSportIconPath(act.sportKey) + ')'"
                      ></span>
                      <span>
                        {{ act.type }}<ng-container *ngIf="act.distance"> &middot; {{ act.distance }}</ng-container>
                      </span>
                    </div>
                    <div class="meta-row">
                      <ion-icon name="ribbon-outline"></ion-icon>
                      <span>{{ formatActivityLevelLabel(act.level) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <i class="pi pi-chevron-right activity-item__arrow"></i>
          </div>
          <button
            class="btn-show-more"
            *ngIf="!showAllActivities && activityMoreCount > 0"
            (click)="onShowMoreActivities()"
          >
            + {{ activityMoreCount }} more
            <i class="pi pi-chevron-down"></i>
          </button>
          <div class="section-empty" *ngIf="!activitiesLoading && visibleActivities.length === 0">
            {{ 'userDetail.noActivities' | translate }}
          </div>
          <div class="section-loading" *ngIf="activitiesLoading && visibleActivities.length === 0">
            {{ 'userDetail.loadingActivities' | translate }}
          </div>
        </div>
      </section>
      <section class="section">
        <div class="section__title-row">
          <span class="section__title">{{ 'userDetail.groups' | translate }}</span>
        </div>
        <div class="tab-bar">
          <button class="tab-btn" [class.tab-btn--active]="activeGroupTab === 0" (click)="onGroupTabChange(0)">
            {{ 'userDetail.created' | translate }} ({{ groupTabTotals[0] ?? 0 }})
          </button>
          <button class="tab-btn" [class.tab-btn--active]="activeGroupTab === 1" (click)="onGroupTabChange(1)">
            {{ 'userDetail.joined' | translate }} ({{ groupTabTotals[1] ?? 0 }})
          </button>
        </div>
        <div class="group-list">
          <div
            class="group-item"
            *ngFor="let group of visibleGroups"
            (click)="openGroupDetail(group)"
            style="cursor: pointer"
          >
            <div class="group-item__img" [class.group-item__img--sport]="isSportIconUrl(group.image)">
              <div *ngIf="isSportIconUrl(group.image); else groupPhoto" class="sport-icon-thumb-wrap">
                <span
                  class="sport-icon-thumb"
                  [style.mask-image]="'url(' + group.image + ')'"
                  [style.-webkit-mask-image]="'url(' + group.image + ')'"
                ></span>
              </div>
              <ng-template #groupPhoto>
                <img [src]="group.image" alt="" (error)="onGroupImageError($event, group)" />
              </ng-template>
            </div>

            <div class="group-item__content">
              <div class="group-item__heading">
                <div class="group-item__title">{{ group.title }}</div>
                <span class="group-badge">{{ 'userDetail.group' | translate }}</span>
              </div>

              <div class="group-item__meta-wrapper">
                <div class="group-item__meta">
                  <div class="meta-row">
                    <i class="pi pi-map-marker"></i>
                    <span>{{ group.location || 'Unknown' }}</span>
                  </div>
                  <div class="meta-row">
                    <i class="pi pi-users"></i>
                    <span>{{ group.membersCount ?? 0 }} members</span>
                  </div>
                </div>
                <div class="group-item__meta">
                  <div class="meta-row">
                    <span
                      class="sport-icon-group"
                      [style.mask-image]="'url(' + getSportIconPath(group.sportKey) + ')'"
                      [style.-webkit-mask-image]="'url(' + getSportIconPath(group.sportKey) + ')'"
                    ></span>
                    <span>{{ group.type || '—' }}</span>
                  </div>
                  <div class="meta-row">
                    <i class="pi pi-sitemap"></i>
                    <span>{{ group.subgroupsCount ?? 0 }} subgroups</span>
                  </div>
                </div>
              </div>
            </div>

            <i class="pi pi-chevron-right group-item__arrow"></i>
          </div>

          <button class="btn-show-more" *ngIf="!showAllGroups && groupMoreCount > 0" (click)="onShowMoreGroups()">
            + {{ groupMoreCount }} more
            <i class="pi pi-chevron-down"></i>
          </button>
          <div class="section-empty" *ngIf="!groupsLoading && visibleGroups.length === 0">
            {{ 'userDetail.noGroups' | translate }}
          </div>
          <div class="section-loading" *ngIf="groupsLoading && visibleGroups.length === 0">
            {{ 'userDetail.loadingGroups' | translate }}
          </div>
        </div>
      </section>
      </div>
    </div>
    <div class="drawer__footer">
      <button *ngIf="allowDelete" class="btn-delete" (click)="openDeleteDialog()"><i class="pi pi-trash"></i> {{ 'userDetail.deleteAccount' | translate }}</button>
      <div class="drawer__footer-actions">
        <ng-container *ngIf="unsavedChanges; else footerCloseOnly">
          <button class="btn-discard" (click)="discardAll()">{{ 'userDetail.discardAll' | translate }}</button>
          <button class="btn-save" [disabled]="hasPersonalFormError || saving" (click)="saveAll()">
            <i class="pi pi-save"></i> {{ saving ? 'Saving...' : 'Save all changes' }}
          </button>
        </ng-container>
        <ng-template #footerCloseOnly>
          <button class="btn-close" type="button" (click)="close()">{{ 'userDetail.close' | translate }}</button>
        </ng-template>
      </div>
    </div>
  </ng-container>
</div>